import React from 'react';
import { Row, Col, Card, Table, Button, Badge, Typography } from 'antd';
import {
  TeamOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  CarOutlined,
  RocketOutlined,
  PercentageOutlined,
  EyeOutlined,
  RiseOutlined
} from '@ant-design/icons';

import './Dashboard.css';

const { Title, Text } = Typography;

const WhiteLabelDashboard = () => {
  
  // Managed Clients Data
  const managedClients = [
    {
      id: 1,
      name: 'ProShine Auto Detailing',
      locations: 1,
      vehicles: 5,
      mrr: '$733',
      commission: '$293.2',
      status: 'active'
    },
    {
      id: 2,
      name: 'Blue Ridge HVAC',
      locations: 3,
      vehicles: 18,
      mrr: '$2,206',
      commission: '$882.4',
      status: 'active'
    }
  ];

  const columns = [
    {
      title: 'Client Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Locations',
      dataIndex: 'locations',
      key: 'locations',
      align: 'center'
    },
    {
      title: 'Vehicles',
      dataIndex: 'vehicles',
      key: 'vehicles',
      align: 'center'
    },
    {
      title: 'MRR',
      dataIndex: 'mrr',
      key: 'mrr',
      align: 'right'
    },
    {
      title: 'Commission',
      dataIndex: 'commission',
      key: 'commission',
      align: 'right',
      render: (text) => <Text strong style={{ color: '#16a34a' }}>{text}</Text>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status) => <Badge status="success" text="active" />
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      render: () => (
        <Button type="link" size="small" icon={<EyeOutlined />}>
          View as Client
        </Button>
      )
    }
  ];

  return (
    <div className="reseller-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <Title level={2} style={{ margin: 0 }}>Dashboard</Title>
          <Text type="secondary">ServiceTech Marketing Partners</Text>
        </div>
        <div className="last-updated">Last updated: 6:53:54 PM</div>
      </div>

      {/* Earnings Card */}
      <Card className="earnings-card">
        <div className="earnings-header">
          <div>
            <Text type="secondary">Year-to-Date Earnings (2026)</Text>
            <div className="earnings-amount">$0</div>
            <div className="earnings-breakdown">
              <Text type="secondary">$0 paid • $0 pending</Text>
            </div>
          </div>
          <div className="earnings-icon">
            <RiseOutlined />
          </div>
        </div>
      </Card>

      {/* Stats Grid */}
      <Row gutter={16} className="stats-grid">
        <Col span={6}>
          <Card className="stat-card">
            <div className="stat-icon">
              <TeamOutlined />
            </div>
            <div className="stat-content">
              <Text type="secondary">Managed Clients</Text>
              <div className="stat-value">2</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <div className="stat-icon">
              <DollarOutlined />
            </div>
            <div className="stat-content">
              <Text type="secondary">Total MRR</Text>
              <div className="stat-value">$2,939</div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <div className="stat-icon">
              <PercentageOutlined />
            </div>
            <div className="stat-content">
              <Text type="secondary">Commission Rate</Text>
              <div className="stat-value">40%</div>
              <Text type="secondary" style={{ fontSize: 12 }}>Both products sold</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card">
            <div className="stat-icon">
              <DollarOutlined />
            </div>
            <div className="stat-content">
              <Text type="secondary">Est. Payout</Text>
              <div className="stat-value">$1,175.6</div>
              <Text type="secondary" style={{ fontSize: 12 }}>This month</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Additional Stats */}
      <Row gutter={16} className="additional-stats">
        <Col span={6}>
          <Card className="stat-card-small">
            <EnvironmentOutlined className="stat-icon-small" />
            <div>
              <div className="stat-value-small">4</div>
              <Text type="secondary">Total Locations</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card-small">
            <CarOutlined className="stat-icon-small" />
            <div>
              <div className="stat-value-small">23</div>
              <Text type="secondary">Total Vehicles</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card-small">
            <RocketOutlined className="stat-icon-small" />
            <div>
              <div className="stat-value-small">5</div>
              <Text type="secondary">Active Campaigns</Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card className="stat-card-small">
            <PercentageOutlined className="stat-icon-small" />
            <div>
              <div className="stat-value-small">2.69%</div>
              <Text type="secondary">Avg CTR</Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Managed Clients Table */}
      <Card className="managed-clients-card">
        <div className="card-header">
          <div>
            <Title level={4} style={{ margin: 0 }}>Managed Clients</Title>
            <Text type="secondary">View and manage your client accounts</Text>
          </div>
        </div>
        <Table
          columns={columns}
          dataSource={managedClients}
          rowKey="id"
          pagination={false}
        />
      </Card>

      {/* Commission Breakdown */}
      <Card className="commission-breakdown-card">
        <Title level={4}>Commission Breakdown</Title>
        <Text type="secondary">Your earnings from managed clients</Text>
        
        <div className="commission-items">
          <div className="commission-item">
            <div>
              <Text strong>ProShine Auto Detailing</Text>
              <br />
              <Text type="secondary">$733/mo × 40% commission</Text>
            </div>
            <Text strong className="commission-amount">$293.2/mo</Text>
          </div>
          
          <div className="commission-item">
            <div>
              <Text strong>Blue Ridge HVAC</Text>
              <br />
              <Text type="secondary">$2,206/mo × 40% commission</Text>
            </div>
            <Text strong className="commission-amount">$882.4/mo</Text>
          </div>
          
          <div className="commission-total">
            <Text strong>Total Monthly Commission</Text>
            <Text strong className="total-amount">$1,175.6</Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default WhiteLabelDashboard;
